(() => {
  const currency = () => window.Shopify?.currency?.active || 'USD';
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: currency(),
  }).format(Number(cents || 0) / 100);

  const initReveals = (scope = document) => {
    const elements = [...scope.querySelectorAll('.bmb-reveal:not([data-bmb-reveal-ready])')];
    if (!elements.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.14 });
    elements.forEach((element) => {
      element.dataset.bmbRevealReady = 'true';
      observer.observe(element);
    });
  };

  const initHeader = (scope = document) => {
    scope.querySelectorAll('[data-bmb-header]:not([data-bmb-ready])').forEach((header) => {
      header.dataset.bmbReady = 'true';
      const openButton = header.querySelector('[data-bmb-menu-open]');
      const menu = document.getElementById(openButton?.getAttribute('aria-controls'));
      const backdrop = document.querySelector('.bmb-menu-backdrop');
      if (!openButton || !menu || !backdrop) return;
      let lastFocus;
      const close = () => {
        menu.classList.remove('is-open');
        menu.setAttribute('aria-hidden', 'true');
        openButton.setAttribute('aria-expanded', 'false');
        backdrop.hidden = true;
        document.body.classList.remove('overflow-hidden');
        lastFocus?.focus();
      };
      const open = () => {
        lastFocus = document.activeElement;
        menu.classList.add('is-open');
        menu.setAttribute('aria-hidden', 'false');
        openButton.setAttribute('aria-expanded', 'true');
        backdrop.hidden = false;
        document.body.classList.add('overflow-hidden');
        menu.querySelector('button, a')?.focus();
      };
      openButton.addEventListener('click', open);
      menu.querySelectorAll('[data-bmb-menu-close]').forEach((button) => button.addEventListener('click', close));
      menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
      backdrop.addEventListener('click', close);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && menu.classList.contains('is-open')) close();
      });
    });
  };

  const initFilmstrips = (scope = document) => {
    scope.querySelectorAll('[data-bmb-filmstrip]:not([data-bmb-ready])').forEach((filmstrip) => {
      filmstrip.dataset.bmbReady = 'true';
      const button = filmstrip.querySelector('[data-bmb-filmstrip-toggle]');
      if (!button) return;
      button.addEventListener('click', () => {
        const paused = filmstrip.classList.toggle('is-paused');
        button.setAttribute('aria-pressed', String(paused));
        button.setAttribute('aria-label', paused ? button.dataset.playLabel : button.dataset.pauseLabel);
        button.querySelector('[data-bmb-pause-icon]').hidden = paused;
        button.querySelector('[data-bmb-play-icon]').hidden = !paused;
      });
    });
  };

  const initOffers = (scope = document) => {
    scope.querySelectorAll('[data-bmb-offer]:not([data-bmb-ready])').forEach((dialog) => {
      dialog.dataset.bmbReady = 'true';
      const seenKey = `bmb-offer-seen-${dialog.id}`;
      const close = () => {
        if (dialog.open) dialog.close();
        sessionStorage.setItem(seenKey, 'yes');
      };
      dialog.querySelectorAll('[data-bmb-offer-close]').forEach((button) => button.addEventListener('click', close));
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) close();
      });
      dialog.addEventListener('cancel', () => sessionStorage.setItem(seenKey, 'yes'));
      if (!sessionStorage.getItem(seenKey)) {
        window.setTimeout(() => {
          if (!dialog.open && document.visibilityState === 'visible') dialog.showModal();
        }, Number(dialog.dataset.delay || 11000));
      }
    });
  };

  const updateProduct = (root) => {
    const select = root.querySelector('[data-bmb-variant-select]');
    const option = select?.selectedOptions?.[0];
    const quantityInput = root.querySelector('[data-bmb-quantity] input');
    const cents = Number(option?.dataset.priceCents || root.dataset.priceCents || 0);
    const available = option ? option.dataset.available === 'true' : root.dataset.available === 'true';
    const quantity = Math.max(1, Number(quantityInput?.value || 1));
    const price = root.querySelector('[data-bmb-product-price]');
    const compare = root.querySelector('[data-bmb-compare-price]');
    const stock = root.querySelector('[data-bmb-stock-status]');
    const submit = root.querySelector('button[type="submit"][name="add"]');
    if (price) price.textContent = option?.dataset.price || money(cents);
    if (compare) {
      compare.textContent = option?.dataset.comparePrice || '';
      compare.hidden = !option?.dataset.comparePrice;
    }
    if (stock) {
      stock.classList.toggle('is-sold-out', !available);
      const label = stock.querySelector('strong');
      if (label) label.textContent = available ? root.dataset.inStockText : root.dataset.soldOutText;
    }
    if (submit) {
      submit.disabled = !available;
      submit.toggleAttribute('aria-disabled', !available);
      const label = submit.querySelector('[data-bmb-add-label]');
      const total = submit.querySelector('[data-bmb-add-total]');
      if (label) label.textContent = available ? root.dataset.addLabel : root.dataset.soldOutText;
      if (total) total.textContent = money(cents * quantity);
    }
    const mobilePrice = document.querySelector('[data-bmb-mobile-price]');
    if (mobilePrice) mobilePrice.textContent = money(cents);
    const mobileSubmit = document.querySelector(`button[form="${root.querySelector('form')?.id}"]`);
    if (mobileSubmit) mobileSubmit.disabled = !available;
  };

  const initProducts = (scope = document) => {
    scope.querySelectorAll('[data-bmb-product-root]:not([data-bmb-ready])').forEach((root) => {
      root.dataset.bmbReady = 'true';
      root.querySelector('[data-bmb-variant-select]')?.addEventListener('change', () => updateProduct(root));
      const quantity = root.querySelector('[data-bmb-quantity]');
      const input = quantity?.querySelector('input');
      quantity?.querySelector('[data-bmb-quantity-minus]')?.addEventListener('click', () => {
        input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
        updateProduct(root);
      });
      quantity?.querySelector('[data-bmb-quantity-plus]')?.addEventListener('click', () => {
        input.value = Number(input.value || 1) + 1;
        updateProduct(root);
      });
      input?.addEventListener('change', () => {
        input.value = Math.max(Number(input.min || 1), Number(input.value || 1));
        updateProduct(root);
      });
      updateProduct(root);
    });
  };

  const updateExternalProduct = (root) => {
    const input = root.querySelector('[data-bmb-external-quantity] input');
    const quantity = Math.max(1, Number(input?.value || 1));
    const cents = Number(root.dataset.priceCents || 0);
    const storeUrl = String(root.dataset.storeUrl || '').replace(/\/$/, '');
    const variantId = root.dataset.variantId;
    const destination = `${storeUrl}/cart/${variantId}:${quantity}`;
    const total = root.querySelector('[data-bmb-external-total]');
    const add = root.querySelector('[data-bmb-external-add]');
    const mobileAdd = document.querySelector('[data-bmb-external-mobile-add]');
    if (input) input.value = quantity;
    if (total) total.textContent = money(cents * quantity);
    if (add && storeUrl && variantId) add.href = destination;
    if (mobileAdd && storeUrl && variantId) mobileAdd.href = destination;
  };

  const initExternalProducts = (scope = document) => {
    scope.querySelectorAll('[data-bmb-external-product-root]:not([data-bmb-ready])').forEach((root) => {
      root.dataset.bmbReady = 'true';
      const quantity = root.querySelector('[data-bmb-external-quantity]');
      const input = quantity?.querySelector('input');
      quantity?.querySelector('[data-bmb-external-minus]')?.addEventListener('click', () => {
        input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
        updateExternalProduct(root);
      });
      quantity?.querySelector('[data-bmb-external-plus]')?.addEventListener('click', () => {
        input.value = Number(input.value || 1) + 1;
        updateExternalProduct(root);
      });
      input?.addEventListener('input', () => updateExternalProduct(root));
      input?.addEventListener('change', () => updateExternalProduct(root));
      updateExternalProduct(root);
    });
  };

  const init = (scope = document) => {
    initReveals(scope);
    initHeader(scope);
    initFilmstrips(scope);
    initOffers(scope);
    initProducts(scope);
    initExternalProducts(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
